import { useCallback, useEffect, useRef, useState } from 'react'
import { vclog } from './vc-log'

/**
 * Media (a video or a document) presented to the whole room.
 *
 * Rides inside the generic `broadcast` event rather than a dedicated one:
 *
 *   sendEvent('broadcast', { body: { __inmeet: 'share', action: 'start', … } })
 *
 * `broadcast` is the single relay every build of the signaling server has, and
 * the channel has no catch-all clause — an event the server does not implement
 * raises FunctionClauseError and kills the channel process, dropping the sender
 * out of the room. A dedicated `share:start` therefore only works once every
 * server is upgraded; this works today and keeps working after.
 *
 * The server sniffs the envelope when it knows how (see RoomChannel), recording
 * the share so a late joiner can be replayed into it. A server that does not
 * simply relays, and everyone already in the room still sees the share — the
 * presenter covers the late joiner by re-announcing via `announce`.
 *
 * Unlike the whiteboard the URL DOES travel: there is nothing a client could
 * reconstruct it from, since the presenter picked it. Only URLs every
 * participant can resolve are sent — see `shareableUrl`.
 */

export type SharedMediaKind = 'video' | 'document'

/** A playback instruction from whoever is presenting. */
export type SharedMediaControlAction = 'play' | 'pause' | 'seek'

export type SharedMediaCommand = {
  action: SharedMediaControlAction
  /** Where in the video, in seconds. */
  position: number
  /** Monotonic on the RECEIVER, so every accepted command is newer than the
   *  last and applying one twice is impossible. */
  seq: number
  /**
   * A correction rather than an instruction — sent when the presenter
   * re-announces for a joiner. A player already close to this position ignores
   * it, so nobody watching gets a pointless jump every time someone arrives.
   */
  soft?: boolean
  /** The room's play/pause state at that position. Carried on a resync. */
  paused?: boolean
}
export type SharedMediaSource = 'file' | 'embed'

export type SharedMediaEntry = {
  id: string
  kind: SharedMediaKind
  by: string | null
  name: string
  url: string
  source: SharedMediaSource
  startedAt: number
  /** True when the room's playback is stopped where it stands. */
  paused?: boolean
}

export type SharedMediaState = {
  active: boolean
  kind: SharedMediaKind | null
  by: string | null
  name: string
  url: string
  source: SharedMediaSource
  startedAt: number | null
  paused?: boolean
}

/** One entry in the room's shared-file list, as it travels on the wire. */
export type SharedLibraryItem = {
  id: string
  name: string
  url: string
  /** peerId of whoever put it in the list. Decides Present vs Download. */
  sharedBy: string
  sharedByName?: string
  size?: number
  /** videos only */
  kind?: SharedMediaSource
  /** documents only */
  type?: string
  /** documents only: the PDF copy of a PowerPoint, Word or text file, which
   *  is what Present shows. A client that predates it ignores it and offers
   *  the file for download, as before. */
  previewUrl?: string
}

type Args = {
  peerId: string | null
  isModerator: boolean
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  subscribe: (event: string, handler: (payload: unknown) => void) => () => void
  ready: boolean
  /** Someone added a file to the room's list. Held in a ref internally, so an
   *  unstable identity here does not tear down the subscription. */
  onLibraryAdd?: (kind: SharedMediaKind, item: SharedLibraryItem) => void
  onLibraryRemove?: (kind: SharedMediaKind, id: string) => void
}

const IDLE: SharedMediaState = {
  active: false,
  kind: null,
  by: null,
  name: '',
  url: '',
  source: 'file',
  startedAt: null,
}

/**
 * Can every participant resolve this URL?
 *
 * `blob:` (what URL.createObjectURL returns for an upload) is scoped to the
 * document that created it — it is a handle into *this tab's* memory, not an
 * address. Sending one to the room would give everyone else a link that cannot
 * load, and no amount of networking fixes it. `data:` and `file:` are the same
 * story. Callers use this to decide whether a share can leave the machine.
 */
export function shareableUrl(url: string): boolean {
  // A root-relative path is an uploaded attachment: every participant resolves
  // it against their own origin, so it reaches the room. Protocol-relative
  // (`//host`) is excluded — it is an absolute URL in disguise.
  if (url.startsWith('/') && !url.startsWith('//')) return true
  try {
    const u = new URL(url)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

export function useSharedMedia({
  peerId,
  isModerator,
  sendEvent,
  subscribe,
  ready,
  onLibraryAdd,
  onLibraryRemove,
}: Args) {
  // A stack, not a single slot. Presenting a video over someone's document must
  // not destroy the document — closing the video puts it back on the stage.
  // Last element is what is showing.
  const [stack, setStack] = useState<SharedMediaEntry[]>([])

  // The last playback instruction from the presenter. Viewers apply it; the
  // presenter never receives its own (the relay does not echo).
  const [command, setCommand] = useState<SharedMediaCommand | null>(null)

  // Read inside the channel handler, which is subscribed once — a plain
  // closure over `top` would freeze at whatever was presenting when the
  // subscription was made.
  const presenterRef = useRef<string | null>(null)

  // Sequencing is the receiver's: every accepted command gets the next number,
  // so nothing can be applied twice. The sender's own counter is kept only to
  // drop a control that arrives out of order.
  const seqRef = useRef(0)
  const lastSenderSeqRef = useRef(-1)

  const peerIdRef = useRef(peerId)
  peerIdRef.current = peerId

  // Behind refs so a caller passing inline closures does not resubscribe the
  // channel on every render — which would drop events in the gap.
  const addRef = useRef(onLibraryAdd)
  addRef.current = onLibraryAdd
  const removeRef = useRef(onLibraryRemove)
  removeRef.current = onLibraryRemove

  useEffect(() => {
    if (!ready) return
    const off = subscribe('broadcast', (payload) => {
      const wire = (payload ?? {}) as { from?: string; body?: unknown }
      const b = wire.body
      if (!b || typeof b !== 'object') return
      const env = b as Record<string, unknown>
      if (env.__inmeet !== 'share') return

      // ── Room-wide file list ────────────────────────────────────────────
      // Separate from presenting: a file appears in everyone's panel as soon
      // as it is uploaded, and presenting it is a second, explicit step.
      if (env.action === 'library:add' || env.action === 'library:remove') {
        const kind = env.kind
        if (kind !== 'video' && kind !== 'document') return

        if (env.action === 'library:remove') {
          if (typeof env.id === 'string') removeRef.current?.(kind, env.id)
          return
        }
        const item = env.item as SharedLibraryItem | undefined
        if (!item || typeof item.id !== 'string' || typeof item.url !== 'string') return
        // A copy has to be reachable like the file itself, or it is dropped
        // and the file stays download-only.
        const previewUrl =
          typeof item.previewUrl === 'string' && shareableUrl(item.previewUrl) ? item.previewUrl : undefined
        // Trust the channel's `from` over anything self-reported in the body.
        addRef.current?.(kind, {
          ...item,
          previewUrl,
          sharedBy: typeof wire.from === 'string' ? wire.from : item.sharedBy,
        })
        return
      }

      // ── Somebody asking to be caught up ────────────────────────────────
      // Sent by every client once it is listening (below). Each participant
      // answers for what only it holds: its own files, plus the presentation
      // from whoever is presenting the top of the stage.
      if (env.action === 'sync') {
        if (wire.from && wire.from === peerIdRef.current) return
        catchUpRef.current()
        return
      }

      // ── Playback control ───────────────────────────────────────────────
      // Only the presenter may drive playback, so a command is honoured only
      // when it comes from whoever is currently on the stage. `from` is the
      // channel's, not self-reported.
      if (env.action === 'control') {
        if (!wire.from || wire.from !== presenterRef.current) return
        const action = env.control
        if (action !== 'play' && action !== 'pause' && action !== 'seek') return
        const senderSeq = typeof env.seq === 'number' ? env.seq : 0
        if (senderSeq <= lastSenderSeqRef.current) return
        lastSenderSeqRef.current = senderSeq
        seqRef.current += 1
        setCommand({
          action,
          position: typeof env.position === 'number' ? env.position : -1,
          seq: seqRef.current,
          soft: env.soft === true,
        })
        return
      }

      if (env.action === 'stop') {
        vclog('presence', 'media share stopped', { by: wire.from, id: env.id })
        // Somebody else ended this client's share: another presenter's video
        // replaced it, or a moderator closed it. Forget it, or the re-announce
        // for the next joiner would put it back on the stage. Not for our own
        // echo — `start` stops the old video and starts the new one back to
        // back, and that stop comes home after `lastSentRef` names the new one.
        if (
          typeof env.id === 'string' &&
          wire.from !== peerIdRef.current &&
          lastSentRef.current?.id === env.id
        ) {
          lastSentRef.current = null
        }
        // Removed by id so the owner of a buried entry can close it without
        // disturbing whatever is on top. No id (older client) drops the top.
        setStack((prev) =>
          typeof env.id === 'string' ? prev.filter((e) => e.id !== env.id) : prev.slice(0, -1),
        )
        return
      }
      if (env.action !== 'start') return

      const kind = env.kind
      const url = env.url
      const id = env.id
      if (typeof url !== 'string' || typeof id !== 'string') return
      if (kind !== 'video' && kind !== 'document') return

      // A share attributed to THIS client that this session did not start is a
      // ghost: the room still holds what was on the stage when the tab
      // reloaded, the network dropped, or the meeting was left. Rejoining
      // replayed it and put the old video straight back up. Close it rather
      // than present it — leaving a share behind is never what leaving meant.
      if (
        wire.from &&
        peerIdRef.current &&
        wire.from === peerIdRef.current &&
        lastSentRef.current?.id !== id
      ) {
        vclog('presence', 'closing own stale share', { id })
        sendEvent('broadcast', { body: { __inmeet: 'share', action: 'stop', id } })
        setStack((prev) => prev.filter((e) => e.id !== id))
        return
      }

      vclog('presence', 'media share started', { by: wire.from, kind, id })
      const entry: SharedMediaEntry = {
        id,
        kind,
        by: typeof wire.from === 'string' ? wire.from : null,
        name: typeof env.name === 'string' ? env.name : 'Shared media',
        url,
        source: env.source === 'embed' ? 'embed' : 'file',
        startedAt: typeof env.startedAt === 'number' ? env.startedAt : Date.now(),
        paused: env.paused === true,
      }
      // Re-presenting the same item moves it to the top rather than duplicating.
      //
      // An entry that is already here keeps its own startedAt. A re-announce
      // carries a rebased one so a JOINER lands at the right place, and
      // adopting it here would reload the iframe under everyone already
      // watching — every time anyone joined. Those clients are corrected by the
      // soft seek below instead, which they ignore unless they have drifted.
      setStack((prev) => {
        const existing = prev.find((e) => e.id === entry.id)
        if (existing) {
          return [
            ...prev.filter((e) => e.id !== entry.id),
            { ...existing, paused: entry.paused },
          ]
        }
        return [...prev, entry]
      })

      if (env.resync === true) {
        seqRef.current += 1
        setCommand({
          action: 'seek',
          position: typeof env.position === 'number' ? Math.max(0, env.position) : 0,
          seq: seqRef.current,
          soft: true,
          paused: entry.paused,
        })
      }
    })
    // A presentation cannot outlive its presenter.
    //
    // The room's own copy is cleaned server-side when the session ends, which is
    // what stops a late joiner being replayed a ghost. This is the local half of
    // the same rule: everyone still in the meeting drops the entry as soon as
    // the peer is known gone, instead of waiting for a `stop` that a refresh, a
    // dropped connection or a moderator removal never sends. Removing an entry
    // that is already gone is a no-op, so the two halves cannot fight.
    const offLeft = subscribe('media:peer_left', (payload) => {
      const gone = (payload as { peerId?: unknown } | null)?.peerId
      if (typeof gone !== 'string') return
      setStack((prev) => {
        const next = prev.filter((e) => e.by !== gone)
        if (next.length !== prev.length) {
          vclog('presence', 'presenter left — closing their presentation', { by: gone })
        }
        return next
      })
    })

    // Ask the room to catch this client up, now that something is listening.
    //
    // Nothing else reliably does. A file in the panel was only ever re-sent by
    // whoever was presenting, so someone who joined after a video was uploaded
    // but not presented got an empty list. And the server's replay of the
    // stage is pushed the moment the channel joins, before this handler is
    // subscribed, with nothing buffering it in between. Also re-sent after a
    // reconnect, which drops `ready` and brings it back.
    sendEvent('broadcast', { body: { __inmeet: 'share', action: 'sync' } })

    return () => {
      off()
      offLeft()
    }
  }, [ready, subscribe, sendEvent])

  // The stage is the top of the stack; everything below is a fallback waiting
  // to resurface.
  const top: SharedMediaEntry | null = stack.length ? stack[stack.length - 1] : null
  const state: SharedMediaState = top
    ? {
        active: true,
        kind: top.kind,
        by: top.by,
        name: top.name,
        url: top.url,
        source: top.source,
        startedAt: top.startedAt,
        paused: top.paused,
      }
    : IDLE

  // `stop()` with no argument closes the top, read through a ref so the
  // callback identity stays stable.
  const topRef = useRef<SharedMediaEntry | null>(top)
  topRef.current = top
  const stackRef = useRef(stack)
  stackRef.current = stack
  presenterRef.current = top?.by ?? null

  // A command belongs to the video it was sent for. Dropping it when the stage
  // changes stops a pause from the last share being applied to the next one.
  const stagedIdRef = useRef<string | null>(null)
  if (stagedIdRef.current !== (top?.id ?? null)) {
    stagedIdRef.current = top?.id ?? null
    lastSenderSeqRef.current = -1
    if (command !== null) setCommand(null)
  }

  // Held so the presenter can re-announce to someone who joins mid-share on a
  // server that does not record share state (see the note on `announce`).
  const lastSentRef = useRef<Record<string, unknown> | null>(null)

  const start = useCallback(
    (
      kind: SharedMediaKind,
      media: { id?: string; name: string; url: string; source?: SharedMediaSource },
    ) => {
      if (!shareableUrl(media.url)) return false
      // Stable per item so re-presenting the same file replaces its stack entry
      // instead of pushing a duplicate.
      const id = media.id ?? `${kind}-${media.url}`
      // One video at a time. A new one ends whatever video is on the stage,
      // whoever presented it, and the new presenter takes over. Left in place,
      // the old one sat behind a chip where its presenter's controls reached
      // nobody, since the room follows only the newest share's presenter.
      // The same video is stopped too, so pressing Play on it hands it over
      // and starts it again. Sent before the start, so every client drops the
      // old entry first.
      if (kind === 'video') {
        for (const e of stackRef.current) {
          if (e.kind === 'video') {
            sendEvent('broadcast', { body: { __inmeet: 'share', action: 'stop', id: e.id } })
          }
        }
      }
      const body = {
        __inmeet: 'share',
        action: 'start',
        id,
        kind,
        name: media.name,
        url: media.url,
        source: media.source ?? 'file',
        startedAt: Date.now(),
      }
      lastSentRef.current = body
      sendEvent('broadcast', { body })
      return true
    },
    [sendEvent],
  )

  /**
   * Drive playback for the whole room.
   *
   * Only meaningful from the presenter — everyone else ignores commands that
   * do not come from whoever is on the stage. `position` is in seconds and is
   * sent with every action, including play/pause, so a viewer whose playback
   * has drifted is pulled back into line rather than only stopped.
   */
  const controlSeqRef = useRef(0)
  const controlMedia = useCallback(
    (action: SharedMediaControlAction, position: number, soft = false) => {
      const id = topRef.current?.id
      if (!id) return
      controlSeqRef.current += 1
      sendEvent('broadcast', {
        body: {
          __inmeet: 'share',
          action: 'control',
          id,
          control: action,
          // -1 is meaningful: "I could not read my player." Clamping it to 0
          // would tell every viewer to jump to the beginning, which is exactly
          // the bug this carries the distinction to avoid.
          position: position < 0 ? -1 : Math.floor(position),
          seq: controlSeqRef.current,
          // A drift correction rather than something the presenter just did.
          // Viewers already in step ignore it.
          soft,
        },
      })
    },
    [sendEvent],
  )

  /** Close one presentation. Defaults to whatever is on top. */
  const stop = useCallback(
    (id?: string) => {
      const target = id ?? topRef.current?.id
      if (lastSentRef.current?.id === target) lastSentRef.current = null
      sendEvent('broadcast', { body: { __inmeet: 'share', action: 'stop', id: target } })
    },
    [sendEvent],
  )

  /** Re-publish this client's own files, so a late joiner's panel fills in
   *  rather than showing only what is shared after they arrived. */
  const republishLibrary = useCallback(() => {
    for (const [kind, item] of ownLibraryRef.current.values()) {
      sendEvent('broadcast', { body: { __inmeet: 'share', action: 'library:add', kind, item } })
    }
  }, [sendEvent])

  /**
   * Re-send the current share.
   *
   * An updated server records the share and replays it on join, so this is
   * redundant there. Against a server that only relays `broadcast`, it is the
   * only way a participant who arrives mid-presentation ever learns about it —
   * the presenter re-announces when the roster grows.
   */
  const announce = useCallback(() => {
    if (lastSentRef.current) {
      const body = { ...lastSentRef.current }
      const pb = playbackRef.current
      // Re-announce from where the video IS, not where it began. `startedAt` is
      // how a receiver works out its offset, so rebasing it to
      // now-minus-position drops a late joiner exactly where the room is —
      // including after the presenter has paused or skipped, which the original
      // timestamp knows nothing about. Everyone already watching keeps the
      // entry they have, so this does not disturb them.
      if (pb && body.kind === 'video') {
        body.startedAt = Date.now() - pb.position * 1000
        body.paused = pb.paused
        body.position = pb.position
        body.resync = true
      }
      sendEvent('broadcast', { body })
    }
    republishLibrary()
  }, [sendEvent, republishLibrary])

  /**
   * Answer a `sync` request. Everyone re-sends their own files; only whoever
   * presents the top of the stage re-sends the presentation, since an entry
   * re-announced from lower down would move to the top for everyone.
   */
  const catchUpRef = useRef<() => void>(() => {})
  catchUpRef.current = () => {
    if (peerId && presenterRef.current === peerId) announce()
    else republishLibrary()
  }

  /**
   * Close our own share when this tab goes away.
   *
   * Best effort — the socket may already be closing — which is why the two
   * other defences exist: a client refuses to be replayed into its own stale
   * share, and everyone drops a share whose presenter has left the roster.
   * `pagehide` rather than `beforeunload`: it fires for reloads and closes and
   * survives the back/forward cache.
   */
  useEffect(() => {
    const closeOwnShare = () => {
      const id = lastSentRef.current?.id
      if (id) sendEvent('broadcast', { body: { __inmeet: 'share', action: 'stop', id } })
    }
    window.addEventListener('pagehide', closeOwnShare)
    return () => window.removeEventListener('pagehide', closeOwnShare)
  }, [sendEvent])

  /**
   * Drop shares whose presenter is no longer in the room.
   *
   * Only for a peer this client has actually SEEN present: a joiner's roster
   * arrives after the replayed share, and pruning on "not in the list yet"
   * would close a live presentation the moment they walked in.
   */
  const seenPeersRef = useRef(new Set<string>())
  const pruneAbsent = useCallback((presentPeerIds: string[]) => {
    if (!presentPeerIds.length) return
    const present = new Set(presentPeerIds)
    for (const id of present) seenPeersRef.current.add(id)
    setStack((prev) =>
      prev.filter((e) => !e.by || present.has(e.by) || !seenPeersRef.current.has(e.by)),
    )
  }, [])

  /**
   * Where the presenter's own playback has reached. Reported by the player and
   * read only by `announce`, so it never triggers a render.
   */
  const playbackRef = useRef<{ position: number; paused: boolean } | null>(null)
  const updatePlayback = useCallback((position: number, paused: boolean) => {
    playbackRef.current = { position: Math.max(0, Math.floor(position)), paused }
  }, [])

  /** Files THIS client contributed, replayed by `announce`. */
  const ownLibraryRef = useRef(new Map<string, [SharedMediaKind, SharedLibraryItem]>())

  const publishLibraryItem = useCallback(
    (kind: SharedMediaKind, item: SharedLibraryItem) => {
      if (!shareableUrl(item.url)) return false
      ownLibraryRef.current.set(item.id, [kind, item])
      sendEvent('broadcast', { body: { __inmeet: 'share', action: 'library:add', kind, item } })
      return true
    },
    [sendEvent],
  )

  const unpublishLibraryItem = useCallback(
    (kind: SharedMediaKind, id: string) => {
      ownLibraryRef.current.delete(id)
      sendEvent('broadcast', { body: { __inmeet: 'share', action: 'library:remove', kind, id } })
    },
    [sendEvent],
  )

  return {
    ...state,
    /** The whole stack, oldest first. */
    stack,
    /** What is on the stage right now, or null. */
    top,
    /** True when someone ELSE is presenting — the local player follows it. */
    isRemote: state.active && !!peerId && state.by !== peerId,
    canClose: state.active && (isModerator || (!!peerId && state.by === peerId)),
    /** True when THIS client is the one presenting. */
    isPresenter: state.active && !!peerId && state.by === peerId,
    /** The presenter's last playback instruction, for viewers to follow. */
    command,
    start,
    stop,
    controlMedia,
    /** The presenter's player reports its position here, for late joiners. */
    updatePlayback,
    /** Called with the current roster; closes shares left behind by whoever
     *  has gone. */
    pruneAbsent,
    announce,
    publishLibraryItem,
    unpublishLibraryItem,
  }
}
